/**
 * Backs the waitlist dialog in `snippets/sold-out-actions-modal.liquid`, posting
 * to Shopify's `/contact` so requests land in the store's customer-service inbox.
 * No app or external service is involved.
 *
 * Why fetch instead of {% form 'contact' %}
 * -----------------------------------------
 * A native form would reload the page, tearing down the modal it was submitted
 * from, and its `<form>` would be invalid markup nested inside the product
 * section's `<product-form>`. Nothing opens the modal but JavaScript, so there is
 * no no-JS path to preserve: the fields are plain inputs and the values go out
 * through a hidden `<form>` this element creates (see `getForm`).
 *
 * Spam protection
 * ---------------
 * `/contact` answers `400 Missing CAPTCHA token` when the store has spam
 * protection on, and Shopify only mints a token for real `<form>` elements —
 * hence the hidden form, built by script so it can never parse as a nested one.
 *
 * `Shopify.captcha.protect(form)` replaces `form.submit` with a wrapper that runs
 * the challenge, writes the token in, then calls whatever `form.submit` was at
 * bind time. Assigning our own `submit` up front therefore routes the bound flow
 * into `fetch` instead of a page-reloading navigation, and leaves `form.submit()`
 * doing the same fetch when nothing binds (protection off, provider blocked).
 *
 * Success is a redirect to `?contact_posted=true`, which `fetch` follows, so the
 * marker lands on the final `response.url`. Anything else counts as a failure —
 * including a 2xx without it, which is how server-side validation comes back.
 */
if (!customElements.get('waitlist-form')) {
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // International-friendly: optional leading +, then 7-15 digits (the E.164
  // range), after stripping common separators.
  const PHONE_REGEX = /^\+?[0-9]{7,15}$/;

  const REQUIRED_REFS = ['formPanel', 'successPanel', 'nameInput', 'emailInput', 'phoneInput', 'submitBtn'];

  /**
   * Giving up submits without a token, so `/contact` 400s and the shopper sees the
   * generic error — still better than hanging on a script that never arrives.
   */
  const CAPTCHA_BIND_TIMEOUT_MS = 10000;

  /**
   * The binding only calls back once its challenge resolves, and swallows a
   * rejected one (a shopper closing the challenge window) with nothing but a
   * console error, leaving the button on "Sending..." forever.
   */
  const CAPTCHA_SUBMIT_TIMEOUT_MS = 60000;

  /** Matches the `visibility` transition delay .modal carries in main.css. */
  const MODAL_CLOSE_MS = 400;

  /**
   * The hidden form's fields, in the order Shopify expects them.
   *
   * `form_type` and `utf8` are the two params required alongside `contact[*]`;
   * `form_type` doubles as how Shopify's captcha bootstrap recognises the form as
   * one of the kinds it protects (its selector is
   * `form[action*='/contact'] input[name='form_type'][value='contact']`).
   */
  const FORM_FIELDS = [
    ['form_type', 'contact'],
    ['utf8', '✓'],
    ['contact[name]', ''],
    ['contact[email]', ''],
    ['contact[phone]', ''],
    ['contact[body]', '']
  ];

  /** Ordered: a failed submit focuses the first entry whose `isValid` fails. */
  const FIELDS = [
    {
      inputRef: 'nameInput',
      errorRef: 'nameError',
      required: 'nameRequired',
      isValid: (value) => value.length > 0
    },
    {
      inputRef: 'emailInput',
      errorRef: 'emailError',
      required: 'emailRequired',
      invalid: 'emailInvalid',
      isValid: (value) => EMAIL_REGEX.test(value)
    },
    {
      inputRef: 'phoneInput',
      errorRef: 'phoneError',
      required: 'phoneRequired',
      invalid: 'phoneInvalid',
      // Strip everything but digits and +, so any separator style is tolerated.
      isValid: (value) => PHONE_REGEX.test(value.replace(/[^\d+]/g, ''))
    }
  ];

  /**
   * Thrown when the captcha binding never hands the submit back — the challenge
   * was closed, or its script broke mid-flight. Distinguished from a plain
   * failure so the shopper is told to try the verification again rather than
   * being left to guess at "something went wrong".
   */
  class CaptchaTimeoutError extends Error {
    constructor(message) {
      super(message);
      this.name = 'CaptchaTimeoutError';
    }
  }

  /**
   * Thrown in place of a real failure when the shopper walks away from a submit
   * — the modal closed, or the element was torn down — so the catch can stay
   * quiet instead of reporting an error nobody is waiting on.
   */
  class SubmitAbandonedError extends Error {
    constructor(message) {
      super(message);
      this.name = 'SubmitAbandonedError';
    }
  }

  function setField(form, name, value) {
    const field = form.elements.namedItem(name);
    if (field) field.value = value;
  }

  class WaitlistForm extends HTMLElement {
    connectedCallback() {
      if (!this.initialised) {
        this.initialised = true;

        this.refs = {};
        this.querySelectorAll('[data-ref]').forEach((el) => {
          this.refs[el.dataset.ref] = el;
        });

        this.strings = this.parseStrings();
        this.ready = this.checkRequiredRefs();

        if (this.ready) {
          // Validation errors stay hidden until the shopper has tried to submit,
          // so the form isn't pre-littered with errors when the modal opens.
          this.submitAttempted = false;
          this.submitting = false;

          // Bumped per submit, so a submit that has been abandoned can tell that
          // it is no longer the one the shopper is waiting on.
          this.submitToken = 0;

          this.bindEvents();
        }
      }

      if (!this.ready) return;

      this.watchModal();
      this.watchVariantChange();
    }

    disconnectedCallback() {
      clearTimeout(this.resetTimer);

      if (this.modalObserver) {
        this.modalObserver.disconnect();
        this.modalObserver = null;
      }

      if (this.variantChangeTarget) {
        this.variantChangeTarget.removeEventListener('on:variant:change', this.variantChangeHandler);
        this.variantChangeTarget = null;
      }

      this.abandonSubmit();

      // Mounted on <body>, so it outlives this element unless removed by hand.
      // Dropped rather than kept, so a reconnect builds a form the captcha can
      // bind to again — which is why the cached binding goes with it.
      if (this.form) {
        this.form.remove();
        this.form = null;
        this.captchaReady = null;
      }
    }

    /**
     * Without this, closing the modal mid-captcha leaves the button disabled on
     * "Sending..." until the captcha timeout fires up to a minute later, then
     * reports a failure into a dialog already dismissed; on teardown it would
     * POST a request for a form that is gone.
     */
    abandonSubmit() {
      if (!this.submitting) return;

      // Anything still in flight for the old token returns early from here on.
      this.submitToken += 1;

      const pending = this.pendingPost;
      this.pendingPost = null;
      if (pending) pending.reject(new SubmitAbandonedError('waitlist-form: submit abandoned'));

      this.restoreSubmitButton();
    }

    restoreSubmitButton() {
      this.submitting = false;

      const btn = this.refs.submitBtn;
      btn.disabled = false;
      if (this.submitLabel != null) btn.textContent = this.submitLabel;
    }

    /** Falls back to `{}` so a missing attribute blanks the messages rather than throwing. */
    parseStrings() {
      try {
        return JSON.parse(this.dataset.strings || '{}');
      } catch (err) {
        return {};
      }
    }

    /**
     * A merchant who breaks the markup in the theme editor should get a disabled
     * form, not a broken page — so this disables rather than throws.
     */
    checkRequiredRefs() {
      const missing = REQUIRED_REFS.filter((name) => !this.refs[name]);
      if (missing.length === 0) return true;

      if (this.refs.submitBtn) this.refs.submitBtn.disabled = true;
      return false;
    }

    bindEvents() {
      this.refs.submitBtn.addEventListener('click', () => this.submitRequest());

      FIELDS.forEach((field) => {
        const input = this.refs[field.inputRef];
        input.addEventListener('input', () => this.refreshValidation());
        input.addEventListener('keydown', (evt) => this.handleKeydown(evt));
      });
    }

    /**
     * The theme's modal-dialog toggles an `open` attribute and dispatches no
     * event, so the open and close hooks have to come from an observer.
     */
    watchModal() {
      if (this.modalObserver) return;

      const modal = this.closest('modal-dialog');
      if (!modal) return;

      this.modalObserver = new MutationObserver(() => {
        clearTimeout(this.resetTimer);

        if (modal.hasAttribute('open')) {
          // Load and bind the captcha the moment the modal opens rather than at
          // submit time, so the provider script is fetched while the shopper is
          // still typing.
          this.ensureCaptcha();
        } else {
          this.abandonSubmit();

          // Deferred past the closing transition, so the swap back to the fields
          // is never visible behind the fading modal.
          this.resetTimer = setTimeout(() => this.handleModalClose(), MODAL_CLOSE_MS);
        }
      });

      this.modalObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });
    }

    /**
     * Opens the modal on whatever the shopper was already looking at.
     *
     * `on:variant:change` bubbles, so listening on the enclosing product-form
     * scopes this to one product without matching ids: a second product on the
     * page (a quick buy, a featured product) is a separate product-form.
     */
    watchVariantChange() {
      if (this.variantChangeTarget) return;
      if (!this.refs || !this.refs.variantSelect) return;

      this.variantChangeTarget = this.closest('product-form');
      if (!this.variantChangeTarget) return;

      this.variantChangeHandler = this.handleVariantChange.bind(this);
      this.variantChangeTarget.addEventListener('on:variant:change', this.variantChangeHandler);
    }

    handleVariantChange(evt) {
      const select = this.refs.variantSelect;
      const variant = evt.detail && evt.detail.variant;
      if (!select || !variant) return;

      const value = String(variant.id);
      if (Array.from(select.options).some((option) => option.value === value)) select.value = value;
    }

    /**
     * Reopening lands on the fields again rather than the confirmation panel, so a
     * shopper can ask about a second variant or fix a mistyped address without a
     * page reload. Values are left alone — retyping the same details is busywork.
     */
    handleModalClose() {
      this.clearError();
      this.announce('');

      if (this.refs.successPanel.hidden) return;

      this.refs.successPanel.hidden = true;
      this.refs.formPanel.hidden = false;
      this.submitAttempted = false;
      FIELDS.forEach((field) => this.clearFieldError(field));
    }

    /** Submits on Enter, since these fields are not wrapped in a `<form>`. */
    handleKeydown(evt) {
      if (evt.key !== 'Enter') return;
      evt.preventDefault();
      this.submitRequest();
    }

    /** Returns invalid fields in FIELDS order, which is also the focus order. */
    computeErrors() {
      const errors = [];
      FIELDS.forEach((field) => {
        const input = this.refs[field.inputRef];
        const value = input.value.trim();
        const messageKey = value.length === 0 ? field.required : field.invalid;

        if (value.length === 0 || !field.isValid(value)) {
          errors.push({ field, input, message: this.strings[messageKey] || '' });
        }
      });
      return errors;
    }

    renderErrors(errors) {
      const invalid = errors || this.computeErrors();
      FIELDS.forEach((field) => {
        const match = invalid.find((entry) => entry.field === field);
        if (match) this.showFieldError(field, match.message);
        else this.clearFieldError(field);
      });
    }

    showFieldError(field, message) {
      const input = this.refs[field.inputRef];
      const errorEl = this.refs[field.errorRef];

      if (errorEl) {
        const span = errorEl.querySelector('span');
        if (span) span.textContent = message;
        errorEl.hidden = false;
        input.setAttribute('aria-describedby', errorEl.id);
      }
      input.setAttribute('aria-invalid', 'true');
    }

    clearFieldError(field) {
      const input = this.refs[field.inputRef];
      const errorEl = this.refs[field.errorRef];

      if (errorEl) {
        errorEl.hidden = true;
        const span = errorEl.querySelector('span');
        if (span) span.textContent = '';
      }
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    }

    /** No-ops until the first submit, so the form isn't pre-littered with errors. */
    refreshValidation() {
      if (!this.submitAttempted) return;
      this.renderErrors();
    }

    /**
     * Everything the merchant needs goes in `contact[body]`, the field Shopify's
     * contact notification is guaranteed to render. The labels stay English: they
     * are read off an order enquiry in the admin, not by the shopper.
     */
    buildBody() {
      const select = this.refs.variantSelect;
      const option = select && select.selectedOptions[0];
      const variantId = select ? select.value : '';
      const productTitle = this.dataset.productTitle || '';
      const productUrl = this.dataset.productUrl || '';

      const lines = ['Waitlist request from the product page.', ''];
      if (productTitle) lines.push(`Product: ${productTitle}`);
      if (option && option.dataset.variantTitle) lines.push(`Variant: ${option.dataset.variantTitle}`);
      if (option && option.dataset.variantSku) lines.push(`SKU: ${option.dataset.variantSku}`);
      if (productUrl) lines.push(`URL: ${variantId ? `${productUrl}?variant=${variantId}` : productUrl}`);

      return lines.join('\n');
    }

    /**
     * The button stays clickable while the form is invalid: submitting reveals the
     * field errors and focuses the first, which beats a disabled button that never
     * says why.
     */
    async submitRequest() {
      if (this.submitting) return;

      this.submitAttempted = true;

      const errors = this.computeErrors();
      this.renderErrors(errors);

      // Cleared before the early return too, or a leftover "something went wrong"
      // sits above the new field errors, blaming the server for a request that
      // was never sent.
      this.clearError();

      if (errors.length > 0) {
        // Focus only lands on the first invalid field, so announce them all.
        this.announce(errors.map((entry) => entry.message).join('. '));
        errors[0].input.focus();
        return;
      }

      const form = this.getForm();
      setField(form, 'contact[name]', this.refs.nameInput.value.trim());
      setField(form, 'contact[email]', this.refs.emailInput.value.trim());
      setField(form, 'contact[phone]', this.refs.phoneInput.value.trim());
      setField(form, 'contact[body]', this.buildBody());

      const btn = this.refs.submitBtn;
      this.submitLabel = btn.textContent;
      this.submitting = true;
      this.submitToken += 1;
      const token = this.submitToken;
      btn.disabled = true;
      btn.textContent = this.strings.sending || '';

      try {
        // Bound here as well as on modal open: a shopper who fills the form
        // faster than the provider script loads would otherwise post without a
        // token and be told the request failed.
        await this.ensureCaptcha();

        // Either await is long enough for the shopper to close the modal, so each
        // is followed by a check that this is still the live submit.
        if (token !== this.submitToken) return;

        const response = await this.submitThroughCaptcha();
        if (token !== this.submitToken) return;

        // Shopify redirects to `?contact_posted=true` on success, and fetch
        // follows the redirect, so the marker shows up on the final response URL.
        if (!response.ok || !response.url.includes('contact_posted=true')) throw new Error('waitlist-form: rejected');

        this.showSuccess();
      } catch (err) {
        if (err instanceof SubmitAbandonedError || token !== this.submitToken) return;

        console.error('waitlist-form: contact submission failed', err);
        this.showError(err instanceof CaptchaTimeoutError ? this.strings.verification : this.strings.generic);
      } finally {
        // On success the panel is hidden, but the button would still read
        // "Sending..." when the modal reopens. Skipped for an abandoned submit:
        // `abandonSubmit` has restored it, and a newer submit may own it by now.
        if (token === this.submitToken) this.restoreSubmitButton();
      }
    }

    /**
     * Built by script rather than in Liquid so it can never be parsed as a form
     * nested inside the section's product form, and mounted on `<body>` rather
     * than in the modal: `.modal__window` is transformed and scrolls, which would
     * trap and clip the challenge the captcha provider opens inside it.
     */
    getForm() {
      if (!this.form) {
        const form = document.createElement('form');
        form.method = 'post';
        // Locale-prefixed on localized storefronts, and `action` is also half of
        // what the captcha bootstrap matches on (`form[action*='/contact']`).
        form.action = this.dataset.contactUrl || '/contact';
        form.acceptCharset = 'UTF-8';
        form.className = 'waitlist-form__captcha';

        FORM_FIELDS.forEach(([name, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          form.appendChild(input);
        });

        // Captured by the captcha binding as "the original submit" and called
        // once the token is in the form; stands in for it when nothing binds.
        form.submit = this.postForm.bind(this);

        this.form = form;
      }

      if (!this.form.isConnected) document.body.appendChild(this.form);

      return this.form;
    }

    /**
     * Resolves once the provider has bound to the hidden form, or once the bind
     * times out. Idempotent: the promise is cached, and `protect` is a no-op on a
     * form it has already marked.
     */
    ensureCaptcha() {
      if (this.captchaReady) return this.captchaReady;

      const captcha = window.Shopify && window.Shopify.captcha;
      const protect = captcha && captcha.protect;
      const form = this.getForm();

      // Absent when the store has spam protection turned off — there is no token
      // to wait for, and `/contact` accepts the post without one.
      if (typeof protect !== 'function') {
        this.captchaReady = Promise.resolve();
        return this.captchaReady;
      }

      // Never rejects: the promise is cached, so a rejection would stick for the
      // life of the page and fail every later submit. A provider that cannot bind
      // resolves instead and the post goes out tokenless.
      this.captchaReady = new Promise((resolve) => {
        setTimeout(resolve, CAPTCHA_BIND_TIMEOUT_MS);

        try {
          // Called through `captcha` rather than detached, in case the provider's
          // implementation leans on its own `this`.
          protect.call(captcha, form, () => resolve());
        } catch (err) {
          console.error('waitlist-form: could not bind the captcha', err);
          resolve();
        }
      });

      return this.captchaReady;
    }

    /**
     * `form.submit()` is the bound captcha's entry point once `ensureCaptcha` has
     * run: it runs the challenge, writes the token in, and only then reaches
     * `postForm` — which is why the response comes back through `pendingPost`
     * rather than from this call. `submit()` and not `requestSubmit()`, because
     * the bootstrap listens for the submit event document-wide and would post the
     * form a second time, natively.
     */
    submitThroughCaptcha() {
      const form = this.getForm();

      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          this.pendingPost = null;
          reject(new CaptchaTimeoutError('waitlist-form: timed out waiting for the captcha'));
        }, CAPTCHA_SUBMIT_TIMEOUT_MS);

        this.pendingPost = {
          resolve: (response) => {
            clearTimeout(timer);
            resolve(response);
          },
          reject: (error) => {
            clearTimeout(timer);
            reject(error);
          }
        };

        form.submit();
      });
    }

    /**
     * Only ever reached through `form.submit()` — directly when no captcha bound,
     * or from the binding once it has appended the token to the form, which is why
     * the body is built from the form element rather than the refs.
     */
    postForm() {
      const pending = this.pendingPost;
      this.pendingPost = null;

      // A late callback from a submit that already timed out: the shopper has
      // been told it failed, so posting now would sign them up behind their back.
      if (!pending || !this.form) return;

      fetch(this.dataset.contactUrl || '/contact', { method: 'POST', body: new FormData(this.form) }).then(
        pending.resolve,
        pending.reject
      );
    }

    /** Focus moves to the panel so the next Tab lands sensibly and SRs announce it. */
    showSuccess() {
      this.refs.formPanel.hidden = true;
      this.refs.successPanel.hidden = false;
      this.refs.successPanel.focus();
    }

    showError(message) {
      const region = this.refs.errorRegion;
      if (!region) return;

      const span = region.querySelector('span');
      if (span) span.textContent = message || '';
      region.hidden = false;
      // The region sits above the submit button in a modal that can scroll, so on
      // a small screen the message can land off-screen and the failed tap reads
      // as nothing happening. Focus scrolls it into view.
      region.focus();
    }

    clearError() {
      const region = this.refs.errorRegion;
      if (!region) return;

      const span = region.querySelector('span');
      if (span) span.textContent = '';
      region.hidden = true;
    }

    /** Focus lands on one field, so the live region has to carry the rest. */
    announce(message) {
      const region = this.refs.status;
      if (!region) return;

      // Assigning an identical string is not a mutation, so a second failed
      // submit with the same errors would announce nothing at all. Clear, then
      // set on the next frame so the live region always sees a change.
      region.textContent = '';
      if (!message) return;

      requestAnimationFrame(() => {
        region.textContent = message;
      });
    }
  }

  customElements.define('waitlist-form', WaitlistForm);
}
